import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AMBITION_KEYS, formatDate } from '@pk/shared';
import type { AmbitionKey, HouseView } from '@pk/shared';
import { actorOf, useCommand } from '../api/hooks';
import { Btn, ConfirmRow, Inline, ScrollArea, Section, Sheet, Tag } from './ui';
import { IconCheck, IconCrown } from './icons';

/**
 * Home: the house's ambition. Shows the active one with its three milestones (done / next) and the reward of the
 * next milestone, or a picker (cards with requirements) when none is active. Texts live in i18n `ambition.*`.
 */
export function Ambition({ h, day }: { h: HouseView; day: number }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const [confirm, setConfirm] = useState(false);
  const a = h.ambitions;
  if (!a) return null;
  const actor = actorOf(h);

  if (a.active) {
    const key = a.active.key;
    const step = a.active.milestone;
    return (
      <Section title={t('ambition.title')} icon={<IconCrown />} className="ambition">
        <div className="panel ambition-card">
          <div className="ambition-head">
            <b>{t(`ambition.${key}.name`)}</b>
            <Tag tone="gold">{t('ambition.milestone', { n: Math.min(3, step + 1) })}</Tag>
          </div>
          <ol className="ambition-steps">
            {[1, 2, 3].map((n) => {
              const done = n <= step;
              const next = n === step + 1;
              return (
                <li key={n} className={`ambition-step ${done ? 'done' : next ? 'next' : ''}`}>
                  <span className="ambition-mark" aria-hidden="true">{done ? <IconCheck /> : n}</span>
                  <span className="ambition-text">
                    <span>{t(`ambition.${key}.m${n}`)}</span>
                    <span className="small muted">{done ? t('ambition.done') : t('ambition.reward', { reward: t(`ambition.${key}.r${n}`) })}</span>
                  </span>
                </li>
              );
            })}
          </ol>
          {step < 3 && <p className="small ambition-next">{t('ambition.reward', { reward: t(`ambition.${key}.r${step + 1}`) })}</p>}
          {a.xpBoostUntil !== null && a.xpBoostUntil > day && <p className="small muted">{t('ambition.boost', { date: formatDate(a.xpBoostUntil) })}</p>}
          {a.squireXpBoostUntil !== null && a.squireXpBoostUntil > day && <p className="small muted">{t('ambition.squireBoost', { date: formatDate(a.squireXpBoostUntil) })}</p>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <Btn small variant="quiet" onClick={() => setConfirm(true)}>{t('ambition.abandon')}</Btn>
          </div>
          <Sheet open={confirm} onClose={() => { setConfirm(false); cmd.clearError(); }} title={t('ambition.abandon')}>
            <ConfirmRow
              message={t('ambition.abandonConfirm')} busy={cmd.pending}
              onConfirm={async () => { const r = await cmd.run({ type: 'abandon_ambition', actorId: actor }); if (r.ok) setConfirm(false); }}
              onCancel={() => setConfirm(false)}
            />
            <Inline error={cmd.error} />
          </Sheet>
        </div>
      </Section>
    );
  }

  const options = AMBITION_KEYS.filter((k) => !a.done.includes(k));
  // suggested first, then the choosable ones, then the locked ones
  const reasonOf = (k: AmbitionKey) => a.options.find((o) => o.key === k)?.unavailableReason ?? null;
  options.sort((x, y) => Number(y === a.suggested) - Number(x === a.suggested) || Number(reasonOf(x) !== null) - Number(reasonOf(y) !== null));
  return (
    <Section title={t('ambition.choose')} icon={<IconCrown />} className="ambition">
      <p className="small muted ambition-hint">{t('ambition.chooseHint')}</p>
      {options.length === 0 ? <p className="small">{t('ambition.noneLeft')}</p> : (
        <ScrollArea className="ambition-scroll" maxHeight="min(360px, 46dvh)" label={t('ambition.choose')}>
          <div className="ambition-list">
            {options.map((k) => {
              const why = reasonOf(k);
              return (
                <div key={k} className={`panel ambition-card ${why ? 'locked' : ''}`}>
                  <div className="ambition-head">
                    <b>{t(`ambition.${k}.name`)}</b>
                    {k === a.suggested && <Tag tone="green">{t('ambition.suggested')}</Tag>}
                    {k === 'rise_of_a_dynasty' && <Tag tone="blue">{t('ambition.late')}</Tag>}
                  </div>
                  <p className="small muted">{t(`ambition.${k}.summary`)}</p>
                  <ol className="ambition-steps">
                    {[1, 2, 3].map((n) => (
                      <li key={n} className="ambition-step">
                        <span className="ambition-mark" aria-hidden="true">{n}</span>
                        <span className="ambition-text">
                          <span>{t(`ambition.${k}.m${n}`)}</span>
                          <span className="small muted">{t('ambition.reward', { reward: t(`ambition.${k}.r${n}`) })}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                  {why && <p className="small muted">{why}</p>}
                  <div className="row" style={{ justifyContent: 'flex-end' }}>
                    <Btn small variant="primary" disabled={!!why} busy={cmd.pending} onClick={() => { void cmd.run({ type: 'choose_ambition', actorId: actor, ambition: k }); }}>{t('ambition.pick')}</Btn>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
      )}
      <Inline error={cmd.error} />
    </Section>
  );
}
